/**
 * To build this file, run `npx rollup -c rollup-transitional.config.js` in the
 * root directory. This will output test/transitional/cps.js. Then, run
 * `build -t` to copy the build products into the build folder. Run
 * `python3 -m http.server 8000` in the build folder to view the results in
 * your browser by visiting http://localhost:8000/transitional.html.
 *
 * A slow but workable one-liner: `find test/transitional/ -type f | entr -s "npx rollup -c rollup-transitional.config.js && ./build.sh -t"`
 */
export * as domain from './domain';
export * as IIID from './IIID';

// Note that here we're exporting the AssetManager instance, which is the
// default export from the assets module.
import _assets from './assets';
export const assets = _assets;

import waitForPredicate from './control-flow/wait-for-predicate';
export const controlFlow = {
    waitForPredicate,
};

import sizeReady from './html/size-ready';
export const html = {
    sizeReady,
};

import camelToSnake from './json/camel-to-snake';
import omitDefaults from './json/omit-defaults';
export const json = {
    camelToSnake,
    omitDefaults,
}

import * as utilsClassic from './utils';
import animateOpen from './animation/animate-open';
import computeViewingDistance from './view3d/compute-viewing-distance';
import createTechDrawingLabel from './view3d/create-tech-drawing-label';
import directionalPosition from './view3d/directional-position';
import exportForRender from './view3d/export-for-render';
import fixSurfaceProRendering from './view3d/fix-surface-pro-rendering';
import gammaCorrect from './color/gamma-correct';
import lookAtWall from './view3d/look-at-wall';
import makeKitchenLights from './view3d/make-kitchen-lights';
import makeMainLights from './view3d/make-main-lights';
import optimizeDirectionalShadow from './view3d/optimize-directional-shadow';
import toTechnicalDocument from './view3d/to-technical-document';
import defer from './control-flow/defer';
import { rouler } from './animation/rouler';
export const utils = {
    ...utilsClassic,
    animateOpen,
    createTechDrawingLabel,
    directionalPosition,
    gammaCorrect,
    lookAtWall,
    makeKitchenLights,
    makeMainLights,
    optimizeDirectionalShadow,
    toTechnicalDocument,
    rouler,
    defer,
}

import showAllPanelHoles from './view3d/show-all-panel-holes';
export const view3d = {
    computeViewingDistance,
    createTechDrawingLabel,
    directionalPosition,
    exportForRender,
    fixSurfaceProRendering,
    gammaCorrect,
    lookAtWall,
    makeKitchenLights,
    makeMainLights,
    optimizeDirectionalShadow,
    showAllPanelHoles,
    toTechnicalDocument,
}

import * as THREE_CLASSIC from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils';
export const THREE = {
    ... THREE_CLASSIC,
    BufferGeometryUtils,
    GLTFExporter,
    GLTFLoader,
    OBJExporter,
    OBJLoader,
    FontLoader,
    TextGeometry,
};

import ClosetUnitView from './views/closet-unit-view';
import CubbyView from './views/cubby-view';
import DoorView from './views/door-view';
import DrawerView from './views/drawer-view';
import GenericAssetView from './views/generic-asset-view';
import HandleView from './views/handle-view';
import HookRackView from './views/hook-rack-view';
import MurphybedView from './views/murphybed-view';
import ParametricPartView from './views/parametric-part-view';
import RoomView from './views/room-view';
import SectionView from './views/section-view';
import StackLayoutHepler from './views/stack-layout-helper';
import VerticalPanelView from './views/vertical-panel-view';
import WallView from './views/wall-view';
import Marker3DView from './views/marker-3d-view';

export const views = {
    ClosetUnitView,
    CubbyView,
    DoorView,
    DrawerView,
    GenericAssetView,
    HandleView,
    HookRackView,
    MurphybedView,
    ParametricPartView,
    RoomView,
    SectionView,
    StackLayoutHepler,
    VerticalPanelView,
    WallView,
    Marker3DView,
};

export * as prefill from './controllers/prefill';
export * as savedViewControllers from './controllers/saved-view-controllers';

// Tools
import { FirstPersonTool } from './tools/first-person-tool';
import Overhead3DMovementTool from './tools/overhead-3d-movement-tool';
export const tools = {
    FirstPersonTool,
    Overhead3DMovementTool
};

export * as viewer from './apps/viewer/viewer-app';

export * as unitEditor from './apps/unit-editor/unit-editor-app';
export * as unitEditorComponent from './apps/unit-editor/unit-editor-component';

export * as walkIn from './apps/walk-in/walk-in-app';
export * as walkInComponent from './apps/walk-in/walk-in-component';

export * as singleWall from './apps/single-wall/single-wall-app';

import './dev';
import materialFactory from './material-factory';
import parametricCache from './parametric-cache';

export const mfac = materialFactory;
export const pc = parametricCache;
export { overrideMaterialFactory } from './view3d/override-material-factory';
